// Layer 4 (UI) — Ingestion setup, "Reconstruct the plant" (polish.P18 #705,
// locked comp `.impeccable/mocks/decision/ingestion-setup.html` B, "ledger and
// blockers", with A's journey-order band and C's layer word on the gap).
// Header fact · one four-cell band · the ledger of what each artefact put in
// the model beside what blocks the next version · pill tabs (Bind live,
// Ruled and settled, Versions). Every figure comes from
// SetupScene.transform.ts; nothing is derived or typed here.

import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import {
  GlassCard,
  HelpAnchor,
  Pill,
  SurfacePageBody,
  SurfacePageHeader,
  SurfacePageLayout,
  SurfacePageTabs,
  Switch,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '@/design-system';
import { cn } from '@/shared/cn';
import { Commentable } from '@/shared/comments/Commentable';
import { BUTTON_GHOST, BUTTON_PRIMARY, SURFACE_PAGE_TITLE } from '@/shared/ui-class';
import { useRole } from '@/versions/v1/shared/RoleContext';
import { useClaims } from '@/versions/v1/shared/ClaimsProvider';
import { projectTileBySlug } from '@/versions/v1/shared/project-seed';
import { projectSidebarHref } from '@/versions/v1/shared/project-sidebar-nav';
import { ProvenanceChip } from '@/versions/v1/shared/ProvenanceChip';
import type { SourceKind } from '@/versions/v1/schema/source-document';
import { ActorChip } from '../advisory/PipelineStrip';
import { CARD_TABLE, CARD_TABLE_DENSE, RankBar } from '../downtime/DowntimeCard';
import { IngestDialog } from './IngestDialog';
import {
  applyIngested,
  bindLiveByReliability,
  bindLiveTotals,
  bindLiveView,
  bunkerHillSources,
  cardIdForSource,
  contributionCards,
  contributionDetail,
  headerCta,
  ingestJourney,
  isSetupTab,
  ledgerRows,
  reconcileView,
  SESSION_DECISION_DATE,
  SETUP_DEFAULT_TAB,
  setupBand,
  setupCopy,
  setupGate,
  setupHeaderFact,
  setupTabs,
  versionRows,
  type BindLiveView,
  type ContributionDetail,
  type IngestMode,
  type LedgerRow,
  type ReconcileRow,
  type ReconcileView,
  type SetupBandCell,
  type SetupComparison,
  type SetupGate,
  type SetupTabId,
  type VersionRow,
} from './SetupScene.transform';

type DialogTarget = {
  sourceId: string;
  kind: SourceKind;
  mode: IngestMode;
};

const SM_PRIMARY = cn(BUTTON_PRIMARY, 'h-7 rounded-lg px-2.5 text-xs font-semibold');
const SM_GHOST = cn(
  BUTTON_GHOST,
  'h-7 rounded-lg border-[var(--hairline-strong)] bg-transparent px-2.5 text-xs font-semibold text-ink-secondary dark:text-ink-secondary',
);
const CAPS = 'text-xs font-semibold uppercase tracking-[0.07em] text-muted';
const CHIP_PILL = 'whitespace-nowrap px-2 py-px text-[10px] font-semibold tracking-[0.05em]';
/** The changed wash (DESIGN.md § Crimson: whatever just changed). */
const CHANGED_WASH = 'bg-[var(--accent-soft)]';

/** A comparison line: the lead term in 600 ink-secondary, the rest muted. */
function Comparison({ value, className }: { value: SetupComparison; className?: string }) {
  return (
    <span className={cn('text-xs leading-snug tabular-nums text-muted', className)}>
      {value.pre}
      {value.lead ? <span className="font-semibold text-ink-secondary">{value.lead}</span> : null}
      {value.rest}
    </span>
  );
}

/** A card with its title inside, meta and actions on the right (comp `.card`). */
function SetupCard({
  title,
  meta,
  actions,
  flush = false,
  className,
  children,
  id,
}: {
  title: string;
  meta?: ReactNode;
  actions?: ReactNode;
  flush?: boolean;
  className?: string;
  children: ReactNode;
  id?: string;
}) {
  return (
    <GlassCard
      as="section"
      tone="matte"
      id={id}
      aria-label={title}
      className={cn('min-w-0', flush ? 'overflow-hidden p-0' : 'px-4 py-3', className)}
    >
      <header
        className={cn(
          'flex flex-wrap items-center gap-2',
          flush ? 'border-b border-[var(--hairline)] px-4 pb-2.5 pt-3' : 'mb-2.5',
        )}
      >
        <h3 className="text-sm font-semibold text-ink-primary">{title}</h3>
        <span className="flex-1" />
        {meta ? <span className="text-xs tabular-nums text-muted">{meta}</span> : null}
        {actions}
      </header>
      {children}
    </GlassCard>
  );
}

function HeaderFact({ fact }: { fact: ReturnType<typeof setupHeaderFact> }) {
  return (
    <div data-header-fact className="flex min-w-0 flex-col border-l border-[var(--hairline-strong)] pl-4 pr-1">
      <span className={CAPS}>{fact.label}</span>
      <span className="whitespace-nowrap text-lg font-semibold leading-tight tabular-nums text-ink-primary">
        {fact.value}{' '}
        <span className="text-xs font-semibold text-muted">{fact.unit}</span>
      </span>
      <Comparison value={fact.comparison} className="whitespace-nowrap" />
    </div>
  );
}

function SetupBand({ cells }: { cells: readonly SetupBandCell[] }) {
  return (
    <GlassCard as="section" tone="matte" aria-label="Plant model build" data-setup-band className="overflow-hidden p-0">
      <div className="grid grid-cols-2 xl:grid-cols-4">
        {cells.map((cell) => (
          <div
            key={cell.id}
            data-band-cell={cell.id}
            className="relative flex min-h-[6.5rem] min-w-0 flex-col gap-1 border-b border-r border-[var(--hairline)] px-4 pb-3 pt-3 last:border-r-0 xl:border-b-0 [&:nth-child(2)]:border-r-0 xl:[&:nth-child(2)]:border-r"
          >
            {cell.tone ? (
              <span aria-hidden className="absolute inset-x-0 top-0 h-[3px] bg-[var(--state-warning)]" />
            ) : null}
            <span className="truncate text-xs font-semibold uppercase tracking-[0.07em] text-muted">{cell.label}</span>
            <span
              data-band-figure
              className={cn(
                'self-start text-2xl font-semibold leading-tight tracking-[-0.01em] tabular-nums text-ink-primary',
                cell.changed ? cn(CHANGED_WASH, '-ml-1 rounded-md px-1') : null,
              )}
            >
              {cell.figure}
            </span>
            <Comparison value={cell.comparison} />
            {cell.system ? (
              <span className="mt-auto flex pt-1">
                <ActorChip actor="System">{cell.system}</ActorChip>
              </span>
            ) : null}
          </div>
        ))}
      </div>
    </GlassCard>
  );
}

function StatusMark({ row }: { row: LedgerRow }) {
  if (row.status === 'present') {
    return (
      <span className="inline-flex items-center gap-1.5 whitespace-nowrap text-xs text-ink-secondary">
        <i aria-hidden className="size-1.5 rounded-full bg-[var(--state-online)]" />
        {row.statusLabel}
      </span>
    );
  }
  return (
    <Pill tone={row.status === 'missing' ? 'warning' : 'default'} className={CHIP_PILL}>
      {row.statusLabel}
    </Pill>
  );
}

function DetailFacts({ detail }: { detail: ContributionDetail }) {
  return (
    <div className="max-w-[720px]">
      {detail.lines.length > 0 ? (
        <dl className="divide-y divide-[var(--color-divider)]">
          {detail.lines.map((line) => (
            <div key={line.label} title={line.title} className="grid grid-cols-[minmax(120px,max-content)_minmax(0,1fr)] items-baseline gap-2.5 py-1.5 text-xs">
              <dt className="text-ink-secondary">{line.label}</dt>
              <dd className="text-right font-semibold tabular-nums text-ink-primary">{line.value}</dd>
            </div>
          ))}
        </dl>
      ) : null}
      {detail.note ? <p className={cn('text-xs text-muted', detail.lines.length > 0 ? 'pt-1.5' : '')}>{detail.note}</p> : null}
    </div>
  );
}

/** Comp B's ledger columns at 1470; one step tighter below 1440 so the
 * artefact names keep their width beside the blockers card. */
const LEDGER_COLS =
  'grid grid-cols-[minmax(0,1fr)_128px_78px_80px_78px] items-center gap-x-3 min-[1440px]:grid-cols-[minmax(0,1fr)_150px_96px_92px_86px] min-[1440px]:gap-x-3.5';

function Ledger({
  rows,
  details,
  canWrite,
  onOpen,
}: {
  rows: readonly LedgerRow[];
  details: Record<string, ContributionDetail | null>;
  canWrite: boolean;
  onOpen: (row: LedgerRow, mode: IngestMode) => void;
}) {
  const [open, setOpen] = useState<ReadonlySet<string>>(() => new Set());
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Commentable name="ContributionCards">
      <SetupCard title="What each artefact put in the model" flush>
        <div data-ledger className="min-w-0 overflow-x-auto">
          <div className="min-w-[620px]">
            <div className={cn(LEDGER_COLS, 'border-b border-[var(--color-divider)] px-4 py-2')}>
              {['Artefact', 'What it put in', 'How it gets in', 'Status', ''].map((head) => (
                <span key={head || 'actions'} className={cn(CAPS, 'leading-tight min-[1440px]:whitespace-nowrap')}>
                  {head}
                </span>
              ))}
            </div>
            {rows.map((row) => {
              const detail = details[row.id];
              const isOpen = open.has(row.id);
              return (
                <Fragment key={row.id}>
                  <div
                    data-ledger-row={row.id}
                    id={row.id === 'paper' ? 'walkthrough-ingestion-loop-input' : undefined}
                    className={cn(
                      LEDGER_COLS,
                      'relative border-b border-[var(--color-divider)] px-4 py-2 last:border-b-0',
                      row.gap
                        ? 'bg-[color-mix(in_srgb,var(--state-warning)_5%,transparent)] shadow-[inset_3px_0_0_color-mix(in_srgb,var(--state-warning)_50%,transparent)]'
                        : null,
                      row.changed ? CHANGED_WASH : null,
                    )}
                  >
                    <div className="min-w-0">
                      <span className="block truncate text-[13px] font-medium leading-snug text-ink-primary" title={row.kindPrefix ? `${row.kindPrefix} · ${row.title}` : row.title}>
                        {row.kindPrefix ? <span className="font-normal text-muted">{row.kindPrefix} · </span> : null}
                        {row.title}
                      </span>
                      {row.contributionHref ? (
                        <Link
                          to={row.contributionHref}
                          className="block truncate text-xs font-semibold leading-snug text-ink-secondary underline decoration-[var(--hairline-strong)] underline-offset-2 hover:text-ink-primary"
                        >
                          {row.contribution}
                        </Link>
                      ) : (
                        <span className="block truncate text-xs leading-snug text-muted" title={row.contribution}>
                          {row.contribution}
                        </span>
                      )}
                      {row.revisionLine ? (
                        <span data-revision-line className="mt-0.5 block text-xs text-ink-primary">
                          {row.revisionLine}
                        </span>
                      ) : null}
                    </div>
                    <div className="min-w-0 text-[13px] font-medium leading-snug tabular-nums text-ink-primary">
                      {row.evidence}
                      {row.evidenceSub ? <span className="block text-xs font-normal text-muted">{row.evidenceSub}</span> : null}
                    </div>
                    <span className="whitespace-nowrap text-xs text-ink-secondary">{row.how}</span>
                    <span>
                      <StatusMark row={row} />
                    </span>
                    <div className="flex items-center justify-end gap-1.5">
                      {canWrite && row.action === 'ingest' ? (
                        <button type="button" className={SM_PRIMARY} onClick={() => onOpen(row, 'ingest')}>
                          Ingest
                        </button>
                      ) : null}
                      {canWrite && row.action === 'update' ? (
                        <button type="button" className={SM_GHOST} onClick={() => onOpen(row, 'update')}>
                          Update
                        </button>
                      ) : null}
                      {detail ? (
                        <button
                          type="button"
                          aria-label={`Details · ${row.title}`}
                          aria-expanded={isOpen}
                          onClick={() => toggle(row.id)}
                          className="grid size-[22px] place-items-center rounded-md text-muted transition-colors hover:bg-[var(--surface-1)] hover:text-ink-primary"
                        >
                          <ChevronRight
                            aria-hidden
                            className={cn('size-3 transition-transform duration-200 motion-reduce:transition-none', isOpen ? 'rotate-90' : '')}
                          />
                        </button>
                      ) : null}
                    </div>
                  </div>
                  {isOpen && detail ? (
                    <div data-ledger-detail={row.id} className="border-b border-[var(--color-divider)] bg-[var(--surface-1)] px-4 pb-3 pt-0.5">
                      <DetailFacts detail={detail} />
                    </div>
                  ) : null}
                </Fragment>
              );
            })}
          </div>
        </div>
      </SetupCard>
    </Commentable>
  );
}

function ClaimLines({ row }: { row: ReconcileRow }) {
  return (
    <ul className="space-y-0.5">
      {row.claims.map((claim) => (
        <li key={claim.id} className="text-xs leading-snug tabular-nums">
          <span className="font-semibold text-ink-primary">{claim.value}</span>
          {claim.qualifier ? ` ${claim.qualifier}` : null}
          <span className="ml-1.5 text-muted">
            {claim.sourceLabel} · {claim.vintage}
          </span>
        </li>
      ))}
    </ul>
  );
}

function RuleForm({
  row,
  onRule,
  onCancel,
}: {
  row: ReconcileRow;
  onRule: (row: ReconcileRow, claimId: string, reason: string) => void;
  onCancel: () => void;
}) {
  const [pickClaim, setPickClaim] = useState(row.claims[0]?.id ?? '');
  const [reason, setReason] = useState('');
  return (
    <div data-rule-form className="mt-2 flex flex-col gap-2">
      <label className="flex flex-col gap-1 text-xs text-muted">
        Claim
        <select
          className="rounded-md border border-[var(--hairline-strong)] bg-transparent px-2 py-1 text-sm text-ink-primary"
          value={pickClaim}
          onChange={(event) => setPickClaim(event.target.value)}
        >
          {row.claims.map((claim) => (
            <option key={claim.id} value={claim.id}>
              {claim.valueLabel} · {claim.sourceLabel}
            </option>
          ))}
        </select>
      </label>
      <label className="flex flex-col gap-1 text-xs text-muted">
        Reason
        <input
          type="text"
          className="w-full rounded-md border border-[var(--hairline-strong)] bg-transparent px-2 py-1 text-sm text-ink-primary"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </label>
      <div className="flex items-center gap-2">
        <button
          type="button"
          className={SM_PRIMARY}
          disabled={!pickClaim || !reason.trim()}
          onClick={() => onRule(row, pickClaim, reason.trim())}
        >
          Confirm
        </button>
        <button type="button" className={SM_GHOST} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function Blockers({
  disputedRows,
  gate,
  canRule,
  canAccept,
  onRule,
  onAccept,
}: {
  disputedRows: readonly ReconcileRow[];
  gate: SetupGate;
  canRule: boolean;
  canAccept: boolean;
  onRule: (row: ReconcileRow, claimId: string, reason: string) => void;
  onAccept: () => void;
}) {
  const [ruling, setRuling] = useState<string | null>(null);
  return (
    <Commentable name="Reconcile">
      <div id="ingestion-reconcile">
        <SetupCard title={gate.title} meta={<Comparison value={gate.meta} />}>
          {disputedRows.length > 0 ? (
            <div>
              {disputedRows.map((row) => (
                <div
                  key={row.subjectKey}
                  data-disputed-row={row.subjectKey}
                  className="border-b border-[var(--color-divider)] py-2 first:pt-0"
                >
                  <div className="mb-1 flex items-center gap-2">
                    <span className="text-[13px] font-semibold text-ink-primary">{row.subjectLabel}</span>
                    <span className="flex-1" />
                    {canRule ? (
                      <button
                        type="button"
                        className={SM_GHOST}
                        aria-expanded={ruling === row.subjectKey}
                        onClick={() => setRuling(ruling === row.subjectKey ? null : row.subjectKey)}
                      >
                        Rule
                      </button>
                    ) : null}
                  </div>
                  <ClaimLines row={row} />
                  {ruling === row.subjectKey ? (
                    <RuleForm
                      row={row}
                      onRule={(target, claimId, reason) => {
                        onRule(target, claimId, reason);
                        setRuling(null);
                      }}
                      onCancel={() => setRuling(null)}
                    />
                  ) : null}
                </div>
              ))}
            </div>
          ) : null}
          <div
            data-approval-gate
            className={cn(
              'mt-2.5 flex flex-col gap-2 rounded-[14px] border border-[var(--hairline-strong)] px-3.5 py-3',
              gate.canAccept ? 'bg-[var(--surface-2)]' : 'bg-[var(--surface-1)]',
            )}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="text-[13.5px] font-semibold text-ink-primary">{gate.proposition}</span>
              <ActorChip actor="Human">{gate.acceptor}</ActorChip>
            </div>
            <span className="text-xs leading-snug text-muted">{gate.line}</span>
            {canAccept ? (
              <div className="flex flex-wrap items-center gap-2">
                <button type="button" className={BUTTON_PRIMARY} disabled={!gate.canAccept} onClick={onAccept}>
                  {gate.acceptLabel}
                </button>
                {gate.blockedReason ? (
                  <span className="text-xs font-semibold text-ink-secondary">{gate.blockedReason}</span>
                ) : null}
              </div>
            ) : gate.blockedReason ? (
              <span className="text-xs font-semibold text-ink-secondary">{gate.blockedReason}</span>
            ) : null}
          </div>
        </SetupCard>
      </div>
    </Commentable>
  );
}

function BindLiveTab({
  bindLive,
  canConfirm,
  onSetConfirmed,
  onConfirmAll,
}: {
  bindLive: BindLiveView;
  canConfirm: boolean;
  onSetConfirmed: (tagId: string, confirmed: boolean) => void;
  onConfirmAll: () => void;
}) {
  const totals = bindLiveTotals(bindLive);
  const groups = bindLiveByReliability(bindLive);
  return (
    <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
      <div className="min-w-0 xl:col-span-8">
        <Commentable name="BindLive">
          <div id="ingestion-bind-live">
            <SetupCard
              title="Bind live"
              flush
              meta={
                <>
                  <span className="font-semibold text-ink-secondary">
                    {totals.confirmed} of {totals.total}
                  </span>{' '}
                  confirmed
                </>
              }
              actions={
                canConfirm ? (
                  <button type="button" className={SM_GHOST} onClick={onConfirmAll}>
                    Confirm all
                  </button>
                ) : null
              }
            >
              <div data-bind-live-scroll className="max-h-[360px] overflow-y-auto">
                <div className={cn(CARD_TABLE_DENSE, '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4 [&_th]:sticky [&_th]:top-0 [&_th]:z-[1] [&_th]:bg-[var(--surface-2)]')}>
                  <Table wrap={false} className="tabular-nums">
                    <THead>
                      <TR>
                        <TH>Reading</TH>
                        <TH>Tag</TH>
                        <TH>Reliability</TH>
                        <TH>Series</TH>
                        <TH className="text-right">Confirmed</TH>
                      </TR>
                    </THead>
                    <TBody>
                      {bindLive.rows.map((row) => (
                        <TR key={row.tagId} data-bind-row={row.tagId}>
                          <TD className="font-medium !text-ink-primary">{row.reading}</TD>
                          <TD className={row.isGap ? '!text-muted' : 'font-medium !text-ink-primary'}>{row.tag}</TD>
                          <TD>
                            <span className="whitespace-nowrap text-[10px] font-semibold uppercase tracking-[0.05em] text-ink-secondary">
                              {row.reliability}
                            </span>
                          </TD>
                          <TD className="whitespace-nowrap">{row.series}</TD>
                          <TD className="text-right">
                            {row.hasSeries ? (
                              <Switch
                                checked={row.confirmed}
                                disabled={!canConfirm}
                                onCheckedChange={(checked) => onSetConfirmed(row.tagId, checked)}
                                aria-label={`Confirm ${row.reading}`}
                                className={cn('align-middle', row.confirmed ? 'bg-ink-primary' : null)}
                              />
                            ) : null}
                          </TD>
                        </TR>
                      ))}
                    </TBody>
                  </Table>
                </div>
              </div>
              <div className="border-t border-[var(--hairline)] py-1.5 text-center text-xs text-muted">
                scrolls · {bindLive.rows.length} rows
              </div>
            </SetupCard>
          </div>
        </Commentable>
      </div>
      <div className="min-w-0 xl:col-span-4">
        <SetupCard title="Confirmed by reliability">
          <div data-reliability-bars className="flex flex-col gap-2.5">
            {groups.map((group) => (
              <div key={group.key} data-reliability={group.key} className="grid grid-cols-[118px_minmax(0,1fr)_58px] items-center gap-2.5 text-xs">
                <span className="whitespace-nowrap font-medium text-ink-primary">{group.label}</span>
                <RankBar ratio={group.ratio} tone="ink" />
                <span className="whitespace-nowrap text-right tabular-nums">
                  <span className="font-semibold text-ink-primary">{group.confirmed}</span>
                  <span className="text-muted"> of {group.total}</span>
                </span>
              </div>
            ))}
          </div>
        </SetupCard>
      </div>
    </div>
  );
}

function HistoryTab({ reconcile }: { reconcile: ReconcileView }) {
  const [ruledOpen, setRuledOpen] = useState(true);
  const [settledOpen, setSettledOpen] = useState(true);
  const renderRow = (row: ReconcileRow) => (
    <TR key={row.subjectKey} data-history-row={row.subjectKey}>
      <TD className="w-[24%] font-medium !text-ink-primary">{row.subjectLabel}</TD>
      <TD className="w-[38%]">
        <ClaimLines row={row} />
      </TD>
      <TD className="w-[15%]">
        {/* In-band states are quiet: only a disputed or blocking state takes
            colour, so every settled standing reads neutral (row 302). */}
        <Pill tone="default" className={CHIP_PILL}>
          {row.standing}
        </Pill>
      </TD>
      <TD>
        {row.rulingDetail ? (
          <div className="flex flex-col items-start gap-1">
            <span className="flex flex-wrap items-center gap-1.5">
              <ActorChip actor="Human">{row.rulingDetail.by}</ActorChip>
              {row.rulingDetail.provenance ? (
                <ProvenanceChip text="provenance" title={row.rulingDetail.provenance} compact />
              ) : null}
            </span>
            <span className="line-clamp-2 text-xs leading-snug text-ink-secondary" title={row.ruling ?? undefined}>
              {row.rulingDetail.at} · {row.rulingDetail.reason}
            </span>
          </div>
        ) : (
          <span className="text-xs text-muted">—</span>
        )}
      </TD>
    </TR>
  );
  const fold = (label: string, isOpen: boolean, onToggle: () => void) => (
    <TR>
      <TD colSpan={4} className="!py-1.5">
        <button type="button" aria-expanded={isOpen} onClick={onToggle} className="text-xs font-semibold text-ink-secondary hover:text-ink-primary">
          {label} {isOpen ? '↑' : '→'}
        </button>
      </TD>
    </TR>
  );
  return (
    <SetupCard
      title="Ruled and settled"
      flush
      meta={`${reconcile.ruledCount} ruled · ${reconcile.settledCount} settled`}
    >
      <div className={cn(CARD_TABLE, '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4 [&_td:last-child]:pr-4 [&_th:last-child]:pr-4 [&_td]:align-top')}>
        <Table wrap={false} className="table-fixed tabular-nums">
          <THead>
            <TR>
              <TH className="w-[24%]">Subject</TH>
              <TH className="w-[38%]">Claims</TH>
              <TH className="w-[15%]">Standing</TH>
              <TH>Ruling</TH>
            </TR>
          </THead>
          <TBody>
            {reconcile.ruledCount > 0 ? fold(`${reconcile.ruledCount} ruled`, ruledOpen, () => setRuledOpen((v) => !v)) : null}
            {ruledOpen ? reconcile.ruledRows.map(renderRow) : null}
            {reconcile.settledCount > 0 ? fold(`${reconcile.settledCount} settled`, settledOpen, () => setSettledOpen((v) => !v)) : null}
            {settledOpen ? reconcile.settledRows.map(renderRow) : null}
          </TBody>
        </Table>
      </div>
    </SetupCard>
  );
}

function VersionsTab({ reconcile, versions }: { reconcile: ReconcileView; versions: readonly VersionRow[] }) {
  const v = reconcile.version;
  return (
    <SetupCard title="Plant model versions" meta={`accepted by ${v.acceptedByRole}`}>
      <div className="divide-y divide-[var(--color-divider)]">
        {versions.map((row) => (
          <div
            key={row.label}
            data-version-row={row.label}
            className={cn('grid grid-cols-[44px_minmax(0,1fr)_auto] items-baseline gap-2.5 py-2', row.next ? '[&+div]:border-dashed' : '')}
          >
            <span className={cn('text-[15px] font-semibold', row.next ? 'text-muted' : 'text-ink-primary')}>{row.label}</span>
            <span className="text-[13px] text-ink-primary">
              {row.note}
              {row.acceptedAt ? <span className="block text-xs text-muted">accepted {row.acceptedAt}</span> : null}
            </span>
            <span className="text-xs tabular-nums text-muted">{row.diffLine}</span>
          </div>
        ))}
      </div>
      <p className={cn(CAPS, 'mb-0.5 mt-3.5')}>
        What changed · v{v.previousN} → v{v.currentN}
      </p>
      <div className={cn(CARD_TABLE_DENSE, '[&_td:first-child]:pl-4 [&_th:first-child]:pl-4')}>
        <Table wrap={false} className="tabular-nums">
          <THead>
            <TR>
              <TH>Subject</TH>
              <TH>v{v.previousN}</TH>
              <TH>v{v.currentN}</TH>
              <TH>Why</TH>
            </TR>
          </THead>
          <TBody>
            {v.lastDiffRows.map((row) => (
              <TR key={`${row.subject}-${row.after}`} data-diff-row={row.subject}>
                <TD className="font-medium !text-ink-primary">{row.subject}</TD>
                <TD>{row.before}</TD>
                <TD className="font-semibold !text-ink-primary">{row.after}</TD>
                <TD>{row.why}</TD>
              </TR>
            ))}
          </TBody>
        </Table>
      </div>
    </SetupCard>
  );
}

export function IngestionSetupScene() {
  const { projectId } = useParams<{ projectId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const project = projectTileBySlug(projectId);
  const copy = setupCopy(project?.name ?? 'this plant');
  const { can } = useRole();
  const claims = useClaims();
  const [ingestedIds, setIngestedIds] = useState<Set<string>>(() => new Set());
  const [revisedIds, setRevisedIds] = useState<Set<string>>(() => new Set());
  const [dialog, setDialog] = useState<DialogTarget | null>(null);
  const tabParam = searchParams.get('tab');
  const tab: SetupTabId = isSetupTab(tabParam) ? tabParam : SETUP_DEFAULT_TAB;

  const revisedBalance = revisedIds.has('src-balance');
  const canRule = can('claim:rule');
  const canAcceptVersion = can('model:accept');

  const documents = useMemo(() => applyIngested(bunkerHillSources(), ingestedIds), [ingestedIds]);
  const cards = useMemo(
    () => contributionCards(documents, revisedIds, projectId ?? 'alpha'),
    [documents, revisedIds, projectId],
  );
  const details = useMemo(
    () => Object.fromEntries(cards.map((card) => [card.id, contributionDetail(card.id, documents)])),
    [cards, documents],
  );
  const cta = headerCta(cards);
  const reconcile = useMemo(() => reconcileView(claims, revisedBalance), [claims, revisedBalance]);
  const bindLive = useMemo(() => bindLiveView(claims), [claims]);
  const band = setupBand(cards, reconcile, bindLive, ingestedIds);
  const ledger = ledgerRows(cards, ingestedIds);
  const gate = setupGate(reconcile);
  const headerFact = setupHeaderFact(reconcile);
  const tabs = setupTabs(reconcile, bindLive);
  const versions = useMemo(() => versionRows(claims, revisedBalance), [claims, revisedBalance]);

  const dialogCardId = dialog ? cardIdForSource(dialog.sourceId) : null;
  const dialogDocuments = useMemo(() => {
    if (!dialog || dialog.mode !== 'ingest') return documents;
    const next = new Set(ingestedIds);
    next.add(dialog.sourceId);
    return applyIngested(bunkerHillSources(), next);
  }, [dialog, documents, ingestedIds]);
  const dialogContribution = dialogCardId ? contributionDetail(dialogCardId, dialogDocuments) : null;

  useEffect(() => {
    document.title = project ? `${copy.title} · ${project.name}` : `${copy.title} · OptiFlow`;
  }, [copy.title, project]);

  if (!project) {
    return (
      <SurfacePageLayout width="full">
        <SurfacePageBody>
          <SurfacePageHeader title="Plant not found" />
          <Link to="/projects" className="text-sm font-medium text-accent-700 dark:text-accent-400">
            Back to portfolio
          </Link>
        </SurfacePageBody>
      </SurfacePageLayout>
    );
  }

  const openJourney = (sourceId: string, kind: SourceKind, mode: IngestMode) => {
    setDialog({ sourceId, kind, mode });
  };

  const completeJourney = (sourceId: string, mode: IngestMode) => {
    const add = (prev: Set<string>) => {
      const next = new Set(prev);
      next.add(sourceId);
      return next;
    };
    if (mode === 'update') setRevisedIds(add);
    else setIngestedIds(add);
    setDialog(null);
  };

  const selectTab = (value: string) => {
    if (!isSetupTab(value)) return;
    const next = new URLSearchParams(searchParams);
    if (value === SETUP_DEFAULT_TAB) next.delete('tab');
    else next.set('tab', value);
    setSearchParams(next, { replace: true });
  };

  return (
    <SurfacePageLayout width="full">
      <Commentable name="IngestionSetupScene">
        <SurfacePageBody className="space-y-3" width="full">
          <header data-setup-header className="flex flex-wrap items-center gap-x-4 gap-y-2">
            <h1 className={cn(SURFACE_PAGE_TITLE, 'min-w-0')}>{copy.title}</h1>
            <div className="ml-auto flex flex-wrap items-center gap-x-4 gap-y-2">
              <HeaderFact fact={headerFact} />
              <div className="flex items-center gap-2">
                {cta && canRule ? (
                  <button
                    type="button"
                    className={BUTTON_PRIMARY}
                    onClick={() => openJourney(cta.sourceId, cta.kind, 'ingest')}
                  >
                    {cta.label}
                  </button>
                ) : null}
                <Link to={projectSidebarHref(project.slug, 'plant-model')} className={cn(BUTTON_GHOST, 'border-[var(--hairline-strong)]')}>
                  Open plant model
                  <ArrowUpRight aria-hidden className="size-3.5" />
                </Link>
                <HelpAnchor doc="ingestion/setup" label="Ingestion" />
              </div>
            </div>
          </header>

          <SetupBand cells={band} />

          <div className="grid grid-cols-1 gap-3 xl:grid-cols-12">
            <div className="min-w-0 xl:col-span-8">
              <Ledger
                rows={ledger}
                details={details}
                canWrite={canRule}
                onOpen={(row, mode) => {
                  if (row.sourceId && row.kind) openJourney(row.sourceId, row.kind, mode);
                }}
              />
            </div>
            <div className="min-w-0 xl:col-span-4">
              <Blockers
                disputedRows={reconcile.disputedRows}
                gate={gate}
                canRule={canRule}
                canAccept={canAcceptVersion}
                onRule={(row, claimId, reason) => {
                  claims.rule(row.subject, claimId, reason, { role: 'metallurgist', name: 'Met' }, SESSION_DECISION_DATE);
                }}
                onAccept={() => {
                  claims.acceptVersion(SESSION_DECISION_DATE, { role: 'metallurgist', name: 'Met' }, revisedBalance);
                }}
              />
            </div>
          </div>

          <SurfacePageTabs aria-label="Ingestion setup" items={tabs} value={tab} onChange={selectTab} variant="ink" className="pt-1" />

          {tab === 'bind' ? (
            <BindLiveTab
              bindLive={bindLive}
              canConfirm={canRule}
              onSetConfirmed={(tagId, confirmed) => claims.setTagConfirmed(tagId, confirmed)}
              onConfirmAll={() => claims.confirmAllTags(bindLive.seriesTagIds)}
            />
          ) : null}
          {tab === 'history' ? <HistoryTab reconcile={reconcile} /> : null}
          {tab === 'versions' ? <VersionsTab reconcile={reconcile} versions={versions} /> : null}

          {dialog ? (
            <IngestDialog
              open
              onOpenChange={(open) => {
                if (!open) setDialog(null);
              }}
              mode={dialog.mode}
              journey={ingestJourney(dialog.kind)}
              contribution={dialogContribution}
              onComplete={() => completeJourney(dialog.sourceId, dialog.mode)}
            />
          ) : null}
        </SurfacePageBody>
      </Commentable>
    </SurfacePageLayout>
  );
}
